import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ForwardedRef } from 'preact/compat';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'preact/hooks';

import { getInfiniteCursorBasedMediaQueryOptions } from '@/queries/media';
import { Video, VideoV2 } from '@/queries/media/types';
import { getPlaylistQueryOption } from '@/queries/playlists';
import { CursorPagination } from '@/queries/types';
import { useSelector } from '@/redux/helper';

import { FormulaHighlightType, HighlightType, PlaylistProps, PlaylistRef } from './types';

export const FORMULA_HIGHLIGHT_MAP: Record<FormulaHighlightType, number> = {
  human: 1,
  algorithm: 2,
  high_comments: 4,
  high_views: 8,
  high_likes: 16,
  combo_social: 32,
  top_viral: 64,
  high_reactions: 128,
  high_likes_or_reactions: 256,
};

export const DEFAULT_FORMULA_SELECTION: FormulaHighlightType[] = ['human', 'algorithm'];

export const computeFormulaHighlightOverride = (selectedFormulas: FormulaHighlightType[]): number =>
  // eslint-disable-next-line no-bitwise
  selectedFormulas.reduce((acc, f) => acc | FORMULA_HIGHLIGHT_MAP[f], 0);

export const useFormulaFilter = () => {
  const [selectedFormulas, setSelectedFormulas] = useState<FormulaHighlightType[]>(DEFAULT_FORMULA_SELECTION);
  const formulaHighlightOverride = computeFormulaHighlightOverride(selectedFormulas);
  return { selectedFormulas, setSelectedFormulas, formulaHighlightOverride };
};

export const useVideoList = (highlightType: HighlightType, formulaHighlightOverride?: number, pageSize = 12) => {
  const isAdmin = useSelector((state) => state.user.role === 'admin');
  const hasFormulaOverride = formulaHighlightOverride !== undefined;
  const highlightValue = hasFormulaOverride
    ? formulaHighlightOverride.toString()
    : highlightType === 'clip'
    ? (0b11).toString()
    : undefined;

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery({
    ...getInfiniteCursorBasedMediaQueryOptions({
      mediaType: 'video',
      pageSize,
      sort: 'desc',
      videoType: hasFormulaOverride ? 'clip' : highlightType,
      ...(highlightValue
        ? {
            highlight: highlightValue,
            // admins need to see all clips (including already watched) when evaluating
            // highlight formulas, non-admins only see unwatched clips
            ...(!isAdmin ? { isViewed: '0' } : undefined),
          }
        : undefined),
    }),
    enabled: highlightType !== 'highlight' && !(hasFormulaOverride && formulaHighlightOverride === 0),
  });

  const fetchMoreIfNeeded = (index: number) => {
    if (!data || isFetchingNextPage || !hasNextPage) return;
    // fetch more highlights if we are near the end
    if (index > data.list.length - 3) {
      fetchNextPage();
    }
  };

  return {
    // this list must be video since we filtered by mediaType
    data: data as CursorPagination<VideoV2>,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
    fetchMoreIfNeeded,
  };
};

export const usePlaylistState = ({
  playlistIdFromURL,
  play,
  videoIdFromURL,
  onVideoChange,
  ref,
}: PlaylistProps & { ref: ForwardedRef<PlaylistRef> }) => {
  const { data } = useQuery(getPlaylistQueryOption(playlistIdFromURL ?? ''));
  const [video, videoIndex] = useMemo(() => {
    if (!data) return [undefined, undefined];
    const index = data.videos.findIndex((video) => video._id === videoIdFromURL);
    return [data.videos[index], index] as const;
  }, [data, videoIdFromURL]);

  const videoChangeHandler = (video: Video, index: number) => {
    if (!data) return;
    onVideoChange?.(video, index);
  };

  useImperativeHandle(ref, () => ({
    hasNext: () => {
      if (!data || !data.videos.length) return false;
      return data.videos.length > (videoIndex ?? 0) + 1;
    },
    next: () => {
      if (!data || !data.videos.length) return;
      const nextIndex = ((videoIndex ?? 0) + 1) % data.videos.length;
      onVideoChange?.(data.videos[nextIndex], nextIndex);
    },
    stop: () => {},
  }));

  const hasData = (data?.videos.length ?? 0) > 0;
  const onVideoChangeRef = useRef(onVideoChange);
  onVideoChangeRef.current = onVideoChange;
  const videoRef = useRef<{ video: Video; videoIndex: number } | undefined>();
  useEffect(() => {
    if (video && videoIndex != null) {
      videoRef.current = { video, videoIndex };
    } else if (data?.videos[0]) {
      videoRef.current = { video: data?.videos[0], videoIndex: 0 };
    } else {
      videoRef.current = undefined;
    }
  }, [data, video, videoIndex]);
  useEffect(() => {
    if (play && playlistIdFromURL && hasData) {
      if (videoRef.current) {
        onVideoChangeRef.current?.(videoRef.current.video, videoRef.current.videoIndex);
      }
    }
  }, [play, hasData, playlistIdFromURL]);

  return {
    data,
    video,
    videoIndex,
    videoChangeHandler,
  };
};
